import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import type { CustomerCurrentSubscription } from '@/types/customer-plan-management';

interface CurrentSubscriptionCardProps {
  subscription: CustomerCurrentSubscription;
  actions?: ReactNode;
}

function formatDate(value: string | null, locale: string) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(parsed);
}

export function CurrentSubscriptionCard({ subscription, actions }: CurrentSubscriptionCardProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language ?? 'en';

  const statusLabel = useMemo(
    () =>
      t(`customer.planManagement.status.${subscription.displayStatus}`, {
        defaultValue: subscription.statusLabel,
      }),
    [subscription.displayStatus, subscription.statusLabel, t],
  );

  const dateLabel =
    subscription.displayStatus === 'trial' || subscription.displayStatus === 'trial_expired'
      ? t('customer.planManagement.currentSubscription.trialEnds')
      : subscription.displayStatus === 'pending_cancellation'
        ? t('customer.planManagement.currentSubscription.activeUntil')
        : subscription.displayStatus === 'cancelled'
          ? t('customer.planManagement.currentSubscription.endedOn')
        : t('customer.planManagement.currentSubscription.nextBilling');

  const dateValue =
    subscription.displayStatus === 'trial' || subscription.displayStatus === 'trial_expired'
      ? subscription.trialExpiresAt
      : subscription.displayStatus === 'pending_cancellation'
        ? subscription.pendingCancellationEffectiveAt
        : subscription.displayStatus === 'cancelled'
          ? subscription.pendingCancellationEffectiveAt ?? subscription.nextBillingAt
        : subscription.nextBillingAt;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <CardTitle>{t('customer.planManagement.currentSubscription.title')}</CardTitle>
            <CardDescription>{t('customer.planManagement.currentSubscription.description')}</CardDescription>
          </div>
          {actions ? <div className="flex items-center gap-2 sm:pt-1">{actions}</div> : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-lg font-semibold">
              {subscription.planName ?? t('customer.planManagement.currentSubscription.noPlan')}
            </p>
            {subscription.planTier ? (
              <p className="text-sm text-muted-foreground">{subscription.planTier}</p>
            ) : null}
          </div>
          <Badge variant="outline">{statusLabel}</Badge>
        </div>

        <Separator />

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{dateLabel}</p>
            <p className="mt-1 font-medium">{formatDate(dateValue, locale)}</p>
          </div>
          {subscription.seatLimit ? (
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                {t('customer.planManagement.currentSubscription.seats')}
              </p>
              <p className="mt-1 font-medium">
                {t('customer.planManagement.currentSubscription.seatUsage', {
                  used: subscription.seatUsage ?? 0,
                  limit: subscription.seatLimit,
                })}
              </p>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
